"use client";

import {
  AnimatePresence,
  motion,
  useMotionTemplate,
  useSpring,
} from "motion/react";

import {
  ComponentProps,
  MutableRefObject,
  ReactNode,
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";

import Button from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useTenantStore } from "@/stores/tenant-store";

// Source: https://buildui.com/posts/global-progress-in-nextjs#show-me-the-code

const ProgressBarContext = createContext<ReturnType<typeof useProgress> | null>(
  null,
);

interface NavigationGuardConfig {
  enabled: boolean;
  shouldBlockNavigation?: (href: string) => boolean;
  onConfirmNavigation?: (run: () => void) => void;
  onBypassNavigation?: () => void;
  title?: string;
  description?: string;
  cancelText?: string;
  confirmText?: string;
}

interface NavigationOptions {
  replace?: boolean;
  bypassUnsavedChangesGuard?: boolean;
  /** Runs only after guard confirmation; return false to discard a stale request. */
  onBeforeNavigate?: () => boolean | void;
  onCancelNavigation?: () => void;
  /** Replace an older deferred request when the selection changes. */
  replacePendingNavigation?: boolean;
}

interface GuardedNavigationOptions {
  bypassUnsavedChangesGuard?: boolean;
  prepareNavigation?: boolean;
  onCancelNavigation?: () => void;
  replacePendingNavigation?: boolean;
}

interface PendingNavigation {
  guard: NavigationGuardConfig;
  prepareNavigation: boolean;
  run: () => void;
  onCancelNavigation?: () => void;
}

interface NavigationGuardContextValue {
  registerNavigationGuard: (
    id: symbol,
    guard: NavigationGuardConfig,
  ) => () => void;
  requestNavigationConfirmation: (
    guard: NavigationGuardConfig,
    run: () => void,
    options?: GuardedNavigationOptions,
  ) => void;
  requestGuardedNavigation: (
    href: string,
    run: () => void,
    options?: GuardedNavigationOptions,
  ) => void;
}

const NavigationGuardContext =
  createContext<NavigationGuardContextValue | null>(null);

export function useProgressBar() {
  const progress = useContext(ProgressBarContext);

  if (progress === null) {
    throw new Error("Need to be inside provider");
  }

  return progress;
}

function useNavigationGuardContext() {
  const context = useContext(NavigationGuardContext);

  if (context === null) {
    throw new Error("Need to be inside provider");
  }

  return context;
}

export function useNavigationGuard(guard: NavigationGuardConfig) {
  const { registerNavigationGuard } = useNavigationGuardContext();
  const guardIdRef = useRef(Symbol("navigation-guard"));

  useEffect(() => {
    return registerNavigationGuard(guardIdRef.current, guard);
  }, [guard, registerNavigationGuard]);
}

export function useNavigationConfirmation() {
  const { requestNavigationConfirmation } = useNavigationGuardContext();

  return requestNavigationConfirmation;
}

function useNavigationGuardController(): NavigationGuardContextValue & {
  pendingGuard: NavigationGuardConfig | null;
  confirmPendingNavigation: () => void;
  cancelPendingNavigation: () => void;
} {
  const guardsRef = useRef(new Map<symbol, NavigationGuardConfig>());
  const pendingNavigationRef = useRef<PendingNavigation | null>(null);
  const [pendingGuard, setPendingGuard] =
    useState<NavigationGuardConfig | null>(null);

  const cancelPendingNavigation = useCallback(() => {
    const pending = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setPendingGuard(null);
    pending?.onCancelNavigation?.();
  }, []);

  const registerNavigationGuard = useCallback(
    (id: symbol, guard: NavigationGuardConfig) => {
      guardsRef.current.set(id, guard);

      return () => {
        guardsRef.current.delete(id);
        if (pendingNavigationRef.current?.guard === guard) {
          cancelPendingNavigation();
        }
      };
    },
    [cancelPendingNavigation],
  );

  const getBlockingGuard = useCallback((href: string) => {
    const guards = Array.from(guardsRef.current.values()).reverse();
    return guards.find((guard) => {
      if (!guard.enabled) return false;
      return guard.shouldBlockNavigation?.(href) ?? false;
    });
  }, []);

  const requestNavigationConfirmation = useCallback(
    (
      guard: NavigationGuardConfig,
      run: () => void,
      options?: GuardedNavigationOptions,
    ) => {
      if (!guard.enabled) {
        run();
        return;
      }

      if (pendingNavigationRef.current) {
        if (!options?.replacePendingNavigation) {
          options?.onCancelNavigation?.();
          return;
        }
        const previous = pendingNavigationRef.current;
        pendingNavigationRef.current = null;
        previous.onCancelNavigation?.();
      }

      pendingNavigationRef.current = {
        guard,
        prepareNavigation: options?.prepareNavigation ?? true,
        run,
        onCancelNavigation: options?.onCancelNavigation,
      };
      setPendingGuard(guard);
    },
    [],
  );

  const requestGuardedNavigation = useCallback(
    (href: string, run: () => void, options?: GuardedNavigationOptions) => {
      const guard = getBlockingGuard(href);

      if (options?.bypassUnsavedChangesGuard) {
        guard?.onBypassNavigation?.();
        run();
        return;
      }

      if (!guard) {
        run();
        return;
      }

      requestNavigationConfirmation(guard, run, options);
    },
    [getBlockingGuard, requestNavigationConfirmation],
  );

  const confirmPendingNavigation = useCallback(() => {
    const pendingNavigation = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setPendingGuard(null);
    if (!pendingNavigation) return;

    if (
      pendingNavigation.prepareNavigation &&
      pendingNavigation.guard.onConfirmNavigation
    ) {
      pendingNavigation.guard.onConfirmNavigation(pendingNavigation.run);
      return;
    }

    pendingNavigation.run();
  }, []);

  return useMemo(
    () => ({
      registerNavigationGuard,
      requestNavigationConfirmation,
      requestGuardedNavigation,
      pendingGuard,
      confirmPendingNavigation,
      cancelPendingNavigation,
    }),
    [
      registerNavigationGuard,
      requestNavigationConfirmation,
      requestGuardedNavigation,
      pendingGuard,
      confirmPendingNavigation,
      cancelPendingNavigation,
    ],
  );
}

export function ProgressBar({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) {
  const progress = useProgress();
  const navigationGuard = useNavigationGuardController();
  const width = useMotionTemplate`${progress.value}%`;
  const pendingGuard = navigationGuard.pendingGuard;

  return (
    <ProgressBarContext.Provider value={progress}>
      <NavigationGuardContext.Provider value={navigationGuard}>
        <AnimatePresence onExitComplete={progress.reset}>
          {progress.state !== "complete" && (
            <motion.div
              style={{ width }}
              exit={{ opacity: 0 }}
              className={className}
            />
          )}
        </AnimatePresence>

        {children}

        <Dialog
          open={!!pendingGuard}
          onOpenChange={(open) => {
            if (!open) navigationGuard.cancelPendingNavigation();
          }}
        >
          <DialogContent className="md:max-w-sm">
            <DialogHeader>
              <DialogTitle>
                {pendingGuard?.title ?? "Leave with unsaved changes?"}
              </DialogTitle>
              <DialogDescription>
                {pendingGuard?.description ??
                  "You have unsaved changes. Leaving this page will discard them."}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={navigationGuard.cancelPendingNavigation}
              >
                {pendingGuard?.cancelText ?? "Keep editing"}
              </Button>
              <Button
                variant="destructive"
                onClick={navigationGuard.confirmPendingNavigation}
              >
                {pendingGuard?.confirmText ?? "Leave without saving"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </NavigationGuardContext.Provider>
    </ProgressBarContext.Provider>
  );
}

export function ProgressBarLink({
  href,
  children,
  onClick,
  isTenantLink = true,
  newWindow = false,
  navigationGuardRef,
  ref,
  ...rest
}: ComponentProps<typeof Link> & {
  isTenantLink?: boolean;
  newWindow?: boolean;
  /** When true at click time, skip client navigation (e.g. after closing a radix dropdown). */
  navigationGuardRef?: MutableRefObject<boolean>;
} & React.RefAttributes<HTMLAnchorElement>) {
  const progress = useProgressBar();
  const { requestGuardedNavigation } = useNavigationGuardContext();
  const router = useRouter();
  const pathname = usePathname();
  const { getCurrentTenant } = useTenantStore();
  const currentTenant = getCurrentTenant();

  let link = href.toString();
  if (isTenantLink && currentTenant) {
    // Check if current pathname already has tenant prepended
    const currentPathHasTenant =
      pathname.startsWith(`/${currentTenant}/`) ||
      pathname === `/${currentTenant}`;

    // If link starts with tenant subdomain, check if it's the actual tenant prefix or just a path segment
    if (link.startsWith(`/${currentTenant}/`) || link === `/${currentTenant}`) {
      // If current pathname also has tenant, this link is relative and needs tenant prepended
      // If current pathname doesn't have tenant, this link already has tenant prepended
      if (currentPathHasTenant) {
        link = `/${currentTenant}${link}`;
      }
    } else {
      // Link doesn't start with tenant, so prepend it
      link = `/${currentTenant}${link}`;
    }
  }

  return (
    <Link
      ref={ref}
      href={link}
      onClick={(e) => {
        if (navigationGuardRef?.current) {
          e.preventDefault();
          return;
        }

        onClick?.(e);

        if (e.defaultPrevented) {
          return;
        }

        if (!e.metaKey && !e.ctrlKey && !newWindow) {
          e.preventDefault();

          requestGuardedNavigation(link, () => {
            progress.start();

            startTransition(() => {
              router.push(link);
              progress.done();
            });
          });
        }
      }}
      className="w-fit"
      target={newWindow ? "_blank" : undefined}
      {...rest}
    >
      {children}
    </Link>
  );
}

function useProgress() {
  const [state, setState] = useState<
    "initial" | "in-progress" | "completing" | "complete"
  >("initial");

  const value = useSpring(0, {
    damping: 25,
    mass: 0.5,
    stiffness: 300,
    restDelta: 0.1,
  });

  useInterval(
    () => {
      // If we start progress but the bar is currently complete, reset it first.
      if (value.get() === 100) {
        value.jump(0);
      }

      const current = value.get();

      let diff;
      if (current === 0) {
        diff = 15;
      } else if (current < 50) {
        diff = rand(1, 10);
      } else {
        diff = rand(1, 5);
      }

      value.set(Math.min(current + diff, 99));
    },
    state === "in-progress" ? 750 : null,
  );

  useEffect(() => {
    if (state === "initial") {
      value.jump(0);
    } else if (state === "completing") {
      value.set(100);
    }

    return value.on("change", (latest) => {
      if (latest === 100) {
        setState("complete");
      }
    });
  }, [value, state]);

  function reset() {
    setState("initial");
  }

  function start() {
    setState("in-progress");
  }

  function done() {
    setState((state) =>
      state === "initial" || state === "in-progress" ? "completing" : state,
    );
  }

  return { state, value, start, done, reset };
}

export function useProgressNavigation() {
  const progress = useProgressBar();
  const { requestGuardedNavigation } = useNavigationGuardContext();
  const router = useRouter();
  const { getCurrentTenant } = useTenantStore();

  const getLink = (href: string, isTenantLink: boolean) =>
    isTenantLink ? `/${getCurrentTenant()}${href}` : href;

  const runWithNavigationGuard = (
    href: string,
    run: () => void,
    isTenantLink: boolean = true,
  ) => {
    const link = getLink(href, isTenantLink);

    requestGuardedNavigation(link, run, { prepareNavigation: false });
  };

  const navigateWithProgress = (
    href: string,
    isTenantLink: boolean = true,
    options?: NavigationOptions,
  ) => {
    const link = getLink(href, isTenantLink);

    requestGuardedNavigation(
      link,
      () => {
        if (options?.onBeforeNavigate?.() === false) return;
        progress.start();
        startTransition(() => {
          // `replace` suits in-page param swaps (e.g. master-detail selection) so
          // clicking through a list doesn't stack a history entry per item.
          if (options?.replace) {
            router.replace(link);
          } else {
            router.push(link);
          }
          progress.done();
        });
      },
      options,
    );
  };

  return { navigateWithProgress, runWithNavigationGuard };
}

function rand(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      savedCallback.current();
    }

    if (delay !== null) {
      tick();

      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}
